import {FootnoteJSON, OptionJSON, T4DDataJSON, VariantJSON} from "../backend.service";
import {Host} from "./host";
import {OrderMethod} from "../constants";
import {getCurrentDate, secondsSinceMidnight} from "../util";

export class SearchResult {

  term: string = "";
  groups: Array<MenuGroup> = [];
  items: Array<MenuItem> = [];

  constructor(init?: Partial<SearchResult>) {
    Object.assign(this, init);
  }

  is_empty(): boolean {
    return (this.groups.length == 0 && this.items.length == 0)
  }

}

export class Menu {
  id: number;
  name: string;
  text: string;
  theme: string;
  root: MenuGroup;
  host: Host;
  method: OrderMethod;
  table_nr: string;
  groups: Record<number, MenuGroup>;
  groupsBySlug: Record<string, MenuGroup>;
  items: Record<number, MenuItem>;
  itemsBySlug: Record<string, MenuItem>;
  options: Record<number, OptionJSON>;
  footnotes: Record<number, FootnoteJSON>;

  constructor(init?: Partial<Menu>) {
    Object.assign(this, init);
  }

  /**
   * initialize a menu from t4ddata object
   * @param data
   */
  static load(data: T4DDataJSON): Menu {
    const menu = new Menu(data.menu);
    // map menu items
    menu.items = {}
    menu.itemsBySlug = {};
    Object.keys(data.items).forEach(k => {
      const item = new MenuItem(menu, data.items[k]);
      menu.items[k] = item
      menu.itemsBySlug[item.slug] = item;
    });
    // map menu groups
    menu.groups = {};
    menu.groupsBySlug = {};
    Object.keys(data.groups).forEach(k => {
      const group = new MenuGroup(menu, data.groups[k]);
      menu.groups[k] = group;
      menu.groupsBySlug[k] = group;
      // link items to group, it's not in t4ddata
      data.groups[k].item_ids.forEach(id => {
        if (menu.items.hasOwnProperty(id)) {
          menu.items[id].group = menu.groups[k];
        }
      });
    });
    menu.options = {};
    Object.keys(data.options).forEach(id => {
      menu.options[id] = data.options[id];
    });
    menu.footnotes = {};
    Object.keys(data.footnotes).forEach(id => {
      menu.footnotes[id] = data.footnotes[id];
    });
    menu.root = menu.groups[data.menu.root_id];
    // remember method
    // note that until we have pulled method selection to front (#271),
    // delivery/takeout cannot be trusted (as the user can change it)
    menu.method = (data.table.type as any as OrderMethod);
    menu.table_nr = data.table.number;
    return menu;
  }

  search(searchTerm: string, show_constrained=false): SearchResult {
    const lterm = searchTerm.toLowerCase(),
      result = new SearchResult({term: searchTerm});
    if (lterm.length >= 2) {
      result.groups = Object.values<MenuGroup>(this.groups)
        .filter(grp => grp.search.match(lterm) && grp.isVisible(show_constrained))
      result.items = Object.values<MenuItem>(this.items)
        .filter(item => item.search.match(lterm) && item.isVisible(show_constrained))
      return result;
    } else {
      return null;
    }
  }

  /**
   * Return a list of weekdays which are relevant for constraints
   * (e.g. list of days where this order can be possibly placed)
   *
   * For inhouse orders or when preorder is disabled, this is only
   * the current day. For other methods, preorder days are taken
   * into account.
   */
  getConstraintDays(): Array<[Date, Date]> {
    if (this.method == OrderMethod.inhouse || !this.host.canPreorder()) {
      return [[getCurrentDate(), getCurrentDate()]];
    } else {
      const times = [];
      this.host.preorder_times.forEach((d) => {
        d.times.forEach((t) => {
          times.push([new Date((d.day + t.start)*1000),
                      new Date((d.day + t.end)*1000)])
        })
      });
      return times;
    }
  }
}

export interface IMenuSection {
  title: string;
  items: Array<MenuItem>;
}


export class Constraint {
  days: Array<number>;      // allowed weekdays; 0=Sunday
  allow_on: string;         // date string, e.g. 2022-03-01
  allow_from: number;       // seconds since midnight
  allow_to: number;         // seconds since midnight
  allow_delivery: boolean;  // allowed for delivery
  allow_takeout: boolean;   // allowed for takeout
  allow_inhouse: boolean;   // allowed for inhouse
  tables: Array<string>;    // allowed at certain tables only
  label: string;        // textual representation

  constructor(init?: Partial<Constraint>) {
    Object.assign(this, init);
  }

  /**
   * check whether dt matches this constraint
   * @param from date from
   * @param to date to
   */
  checkDate(from: Date, to: Date): boolean {

    if (this.allow_on) {
      const ref = new Date(this.allow_on);
      return (ref.getFullYear()==from.getFullYear() &&
             ref.getMonth()==from.getMonth() &&
             ref.getDate()==from.getDate())
          || (ref.getFullYear()==to.getFullYear() &&
             ref.getMonth()==to.getMonth() &&
             ref.getDate()==to.getDate());
    } else {
      // does it match a weekday? (only first day is considered)
      if (!this.days.some(d => d==from.getDay())) {
        return false;
      }
      // do the timespans overlap?
      const tf = secondsSinceMidnight(from),
            tt = secondsSinceMidnight(to);
      //  https://stackoverflow.com/a/13513973
      return (tf <= this.allow_to && this.allow_from <= tt);
    }
  }

  /**
   * check whether one of dates matches this constraint.
   * On the first entry, the time will be considered, but
   * not on the following ones (if any).
   * @param methods possible methods, one must be allowed
   * @param table_nr table number
   * @param dates
   * @return true if constraint is satisfied (e.g. not blocking)
   */
  check(methods: Array<OrderMethod>, table_nr: string, dates: Array<[Date, Date]>): boolean {
    // one of the methods must be allowed
    if ( !(this.allow_inhouse && methods.includes(OrderMethod.inhouse)) &&
         !(this.allow_takeout && methods.includes(OrderMethod.takeout)) &&
         !(this.allow_delivery && methods.includes(OrderMethod.delivery))) {
      return false;
    }
    // check table limitations
    if ( this.tables.length > 0 && !this.tables.includes(table_nr)) {
      return false;
    }

    // check date/time
    return dates.some((dt) => this.checkDate(dt[0], dt[1]));
  }
}

export class MenuGroup {
  id: number;
  slug: string;
  name: string;
  text: string;
  active: boolean;
  search: string;
  image: string;
  parent: number;
  parents: Array<number>;
  children_ids: Array<number>;
  item_ids: Array<number>;
  hidden: boolean;
  constraint?: Constraint;
  menu: Menu;

  constructor(menu: Menu, init?: Partial<MenuGroup>) {
    this.menu = menu;
    Object.assign(this, init);
    this.constraint = (init.constraint) ? new Constraint(init.constraint): null;
  }

  children(with_hidden = false, with_constrained = false): Array<MenuGroup> {
    const qs = this.children_ids.map(id => this.menu.groups[id]);
    if (with_hidden) {
      return qs;
    } else {
      return qs.filter(g => !g.hidden && (!g.isConstrained() || with_constrained));
    }
  }

  items(include_constrained=false): Array<MenuItem> {
    return this.item_ids.filter(id => include_constrained || !this.menu.items[id].isConstrained()).map(id => this.menu.items[id]);
  }

  /**
   * Returns a flattened list of the items in this group.
   */
  item_list(include_hidden=false, include_constrained=false): Array<IMenuSection> {
    let result = [];
    if (!include_hidden && !this.isVisible(include_constrained)) {
      return [];
    }
    if (this.item_ids.length > 0) {
      result.push({
        title: this.name,
        items: this.items(include_constrained)
      })
    }
    this.children(include_hidden, include_constrained).forEach((group) => {
      result = result.concat(group.item_list(include_hidden, include_constrained));
    })
    return result;
  }

  isRoot(): boolean {
    return !this.parent;
  }

  isVisible(show_constrained = false) {
    return !this.hidden && (show_constrained || !this.isConstrained())
  }

  isConstrained(): boolean {
    return this.constraint && !this.constraint.check(
      (this.menu.method==OrderMethod.inhouse)?[OrderMethod.inhouse]:[OrderMethod.takeout, OrderMethod.delivery],
      this.menu.table_nr,
      this.menu.getConstraintDays());
  }

  hasChildren(): boolean {
    return this.children_ids.length > 0;
  }

  hasItems(): boolean {
    return this.item_ids.length > 0;
  }

  getParent(): MenuGroup {
    if (this.parent) {
      return (this.menu.groups[this.parent]);
    } else {
      // we are already root
      return this;
    }
  }

  getParents(with_root: boolean = false): Array<MenuGroup> {
    if (with_root) {
      return this.parents.map(id => this.menu.groups[id]);
    } else {
      return this.parents.map(id => this.menu.groups[id]).slice(1);
    }
  }
}

export interface IChoiceEntry {
  key: string;
  item: MenuItem;
  variant: number;
  price: string;
  title: string;
}

export interface IItemChoice {
  id: number;
  name: string;
  choices: Array<IChoiceEntry>;
}

export class MenuItem {
  menu: Menu;
  group: MenuGroup;
  id: number;
  slug: string;
  name: string;
  int_name: string;
  quantity: string;
  price: string;
  text: string;
  sort: number;
  allow_freetext: boolean;
  active: boolean;
  search: string;
  image: string;
  variants: Array<VariantJSON>;
  option_ids: Array<number>;
  footnote_ids: Array<number>;
  choices: Array<{ id: number, name: string, group: number, free_choice: number }>;
  choices_list: Array<IItemChoice>;
  constraint?: Constraint;

  constructor(menu: Menu, init?: Partial<MenuItem>) {
    this.menu = menu;
    Object.assign(this, init);
    this.constraint = (init.constraint) ? new Constraint(init.constraint): null;
  }

  getParents(): Array<MenuGroup> {
    const result = this.group.getParents();
    result.push(this.group);
    return result;
  }

  isInGroup(group_id): boolean {
    const parents = this.getParents().map(group => group.id);
    return parents.indexOf(group_id) >= 0;
  }

  hasVariants(): boolean {
    return this.variants.length > 0;
  }

  isVisible(show_constrained=false) {
    return this.group.isVisible(show_constrained) && (show_constrained || !this.isConstrained())
  }

  isConstrained(): boolean {
    return this.constraint && !this.constraint.check(
      (this.menu.method==OrderMethod.inhouse)?[OrderMethod.inhouse]:[OrderMethod.takeout, OrderMethod.delivery],
      this.menu.table_nr,
      this.menu.getConstraintDays()
    );
  }

  getDefaultVariantId(): number {
    const first = this.getDefaultVariant();
    return (first) ? first.id : null;
  }

  getDefaultVariant(): VariantJSON {
    return this.variants.find(x => x.active);
  }

  getVariant(id: number): VariantJSON {
    return this.variants.find(x => x.id == id);
  }

  hasOptions(): boolean {
    return this.option_ids.length > 0;
  }

  getOptions(): Array<OptionJSON> {
    return this.option_ids.map(id => this.menu.options[id]);
  }

  getOption(id: number): OptionJSON {
    return this.menu.options[id];
  }

  hasFootnotes(): boolean {
    return this.footnote_ids.length > 0;
  }

  getFootnotes(): Array<FootnoteJSON> {
    return this.footnote_ids.map(id => this.menu.footnotes[id]);
  }

  hasChoices(): boolean {
    return this.choices.length > 0;
  }

  getChoices(): Array<IItemChoice> {
    if (!this.choices_list) {
      this.choices_list = new Array<IItemChoice>();
      for (let choice of this.choices) {
        let choiceitem = {'id': choice.id, 'name': choice.name, 'choices': []}
        for (let section of this.menu.groups[choice.group].item_list(true, false)) {
          for (let item of section.items) {
            if (item.hasVariants()) {
              item.variants.forEach((v) => {
                choiceitem.choices.push({
                  key: `${item.id}-${v.id}`, item: item,
                  variant: v, title: item.name + ' (' + v.name + ')',
                  price: (choice.free_choice == 0) ? v.price : 0
                });
              });
            } else {
              choiceitem.choices.push({
                key: `${item.id}-0`, item: item, variant: null, title: item.name,
                price: (choice.free_choice == 0) ? item.price : 0
              });
            }
          }
        }
        this.choices_list.push(choiceitem);
      }
    }
    return this.choices_list;
  }
}
